import React, {createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState} from "react";

// Pages publish the names of the records they show (event, payer, hotel, participant)
// so the app shell breadcrumb can use them without loading the records a second time.
export type ShellBreadcrumbLabels = Record<string, string>;

type ShellBreadcrumbContextValue = {
    labels: ShellBreadcrumbLabels;
    setLabel: (key: string, label?: string) => void;
};

const ShellBreadcrumbContext = createContext<ShellBreadcrumbContextValue>({
    labels: {},
    setLabel: () => {}
});

export const ShellBreadcrumbProvider: FC<PropsWithChildren> = ({children}) => {
    const [labels, setLabels] = useState<ShellBreadcrumbLabels>({});
    const setLabel = useCallback((key: string, label?: string) => {
        setLabels(current => {
            if (current[key] === label) {
                return current;
            }

            const next = {...current};
            if (label) {
                next[key] = label;
            } else {
                delete next[key];
            }
            return next;
        });
    }, []);
    const value = useMemo(() => ({labels, setLabel}), [labels, setLabel]);

    return <ShellBreadcrumbContext.Provider value={value}>{children}</ShellBreadcrumbContext.Provider>;
};

export const useShellBreadcrumbLabels = () => useContext(ShellBreadcrumbContext).labels;

export const useShellBreadcrumbLabel = (key: string | undefined, label: string | null | undefined) => {
    const {setLabel} = useContext(ShellBreadcrumbContext);
    const trimmed = label?.trim();

    useEffect(() => {
        if (!key) {
            return;
        }

        setLabel(key, trimmed);
        return () => setLabel(key, undefined);
    }, [key, setLabel, trimmed]);
};

export const shellBreadcrumbKeys = {
    event: (id: number | string) => `event:${id}`,
    payer: (id: number | string) => `payer:${id}`,
    hotel: (id: number | string) => `hotel:${id}`,
    participant: (id: number | string) => `participant:${id}`
};
